import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Play, Plus, BarChart3, CheckCircle, ArrowRight, Settings, Target, TrendingUp } from "lucide-react";

const steps = (t: any) => [
  {
    title: t("instruction.steps.step1.title"),
    subtitle: t("instruction.steps.step1.subtitle"),
    description: t("instruction.steps.step1.description"),
    icon: <Plus className="w-12 h-12" />,
    details: {
      title: t("instruction.steps.step1.details.title"),
      items: [
        t("instruction.steps.step1.details.items.0"),
        t("instruction.steps.step1.details.items.1"),
        t("instruction.steps.step1.details.items.2"),
        t("instruction.steps.step1.details.items.3")
      ]
    }
  },
  {
    title: t("instruction.steps.step2.title"),
    subtitle: t("instruction.steps.step2.subtitle"),
    description: t("instruction.steps.step2.description"),
    icon: <Settings className="w-12 h-12" />,
    details: {
      title: t("instruction.steps.step2.details.title"),
      items: [
        t("instruction.steps.step2.details.items.0"),
        t("instruction.steps.step2.details.items.1"),
        t("instruction.steps.step2.details.items.2"),
        t("instruction.steps.step2.details.items.3")
      ]
    }
  },
  {
    title: t("instruction.steps.step3.title"),
    subtitle: t("instruction.steps.step3.subtitle"),
    description: t("instruction.steps.step3.description"),
    icon: <BarChart3 className="w-12 h-12" />,
    details: {
      title: t("instruction.steps.step3.details.title"),
      items: [
        t("instruction.steps.step3.details.items.0"),
        t("instruction.steps.step3.details.items.1"),
        t("instruction.steps.step3.details.items.2"),
        t("instruction.steps.step3.details.items.3"),
        t("instruction.steps.step3.details.items.4")
      ]
    }
  }
];

const UnifiedInstructionPage = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);
  
  // const changeLanguage = (lng: string) => {
  //   i18n.changeLanguage(lng);
  // };

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (autoPlay) {
      interval = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % steps(t).length);
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [autoPlay, t]);

  const handleStepChange = (newStep: number) => {
    if (newStep === currentStep) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(newStep);
      setIsAnimating(false);
    }, 150);
  };

  const next = () => {
    const nextStep = currentStep < steps(t).length - 1 ? currentStep + 1 : 0;
    handleStepChange(nextStep);
  };

  const prev = () => {
    const prevStep = currentStep > 0 ? currentStep - 1 : steps(t).length - 1;
    handleStepChange(prevStep);
  };

  const currentStepData = steps(t)[currentStep];
  const darkBlue = 'rgb(16, 53, 100)';
  const lightBlue = 'rgb(1, 184, 239)';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: darkBlue }}>
            {t("instruction.title")}
          </h1>
          <p className="text-xl max-w-2xl mx-auto" style={{ color: darkBlue, opacity: 0.7 }}>
            {t("instruction.subtitle")}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-12">
          <div className="flex items-center justify-center space-x-4 mb-6">
            {steps(t).map((_, index) => (
              <div key={index} className="flex items-center">
                <button
                  onClick={() => handleStepChange(index)}
                  className={`w-12 h-12 rounded-full border-2 flex items-center justify-center font-semibold transition-all duration-300 ${
                    index === currentStep
                      ? "text-white border-transparent shadow-lg scale-110"
                      : index < currentStep
                      ? "text-white border-transparent"
                      : "bg-white border-2 hover:shadow-md"
                  }`}
                  style={{
                    backgroundColor: index === currentStep 
                      ? lightBlue 
                      : index < currentStep 
                      ? darkBlue 
                      : 'white',
                    borderColor: index <= currentStep ? 'transparent' : darkBlue,
                    color: index <= currentStep ? 'white' : darkBlue
                  }}
                >
                  {index < currentStep ? (
                    <CheckCircle className="w-6 h-6" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </button>
                {index < steps(t).length - 1 && (
                  <div 
                    className="w-16 h-1 mx-2 rounded-full transition-all duration-500"
                    style={{
                      backgroundColor: index < currentStep ? darkBlue : 'rgb(16, 53, 100, 0.2)'
                    }}
                  />
                )}
              </div>
            ))}
          </div>
          
          {/* Progress Percentage */}
          <div className="text-center">
            <span className="text-sm font-medium" style={{ color: darkBlue, opacity: 0.7 }}>
              {t("instruction.progress", {
                current: currentStep + 1,
                total: steps(t).length,
                percent: Math.round(((currentStep + 1) / steps(t).length) * 100)
              })}
            </span>
          </div>
        </div>

        {/* Main Content Card */}
        <div className={`bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-2 transition-all duration-500 ${isAnimating ? 'opacity-50 scale-95' : 'opacity-100 scale-100'}`}
             style={{ 
               borderColor: lightBlue,
               boxShadow: `0 25px 50px -12px rgba(1, 184, 239, 0.15)`
             }}>
          <div className="text-center">
            {/* Icon */}
            <div 
              className="inline-flex items-center justify-center w-24 h-24 rounded-full text-white mb-8 shadow-lg transform transition-transform duration-500 hover:scale-105"
              style={{ backgroundColor: currentStep % 2 === 0 ? lightBlue : darkBlue }}
            >
              {currentStepData.icon}
            </div>

            {/* Content */}
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: darkBlue }}>
              {currentStepData.title}
            </h2>
            <h3 className="text-xl font-semibold mb-6" style={{ color: lightBlue }}>
              {currentStepData.subtitle}
            </h3>
            <p className="text-lg leading-relaxed max-w-2xl mx-auto mb-8" style={{ color: darkBlue, opacity: 0.8 }}>
              {currentStepData.description}
            </p>

            {/* Step-specific Details */}
            <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left max-w-2xl mx-auto">
              <h4 className="font-semibold mb-3" style={{ color: darkBlue }}>{currentStepData.details.title}</h4>
              <ul className="space-y-2 text-sm" style={{ color: darkBlue, opacity: 0.8 }}>
                {currentStepData.details.items.map((item: string, index: number) => (
                  <li key={index}>• {item}</li>
                ))}
              </ul>
            </div>

            {/* Visual Indicator */}
            <div className="flex justify-center mb-8">
              <div 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: lightBlue }}
              ></div>
            </div>

            {/* Step Status Icons */}
            <div className="flex justify-center space-x-8 mb-6">
              <div className="flex items-center space-x-2">
                <Target className={`w-5 h-5 ${currentStep >= 0 ? 'text-green-500' : 'text-gray-400'}`} />
                <span className={`text-sm ${currentStep >= 0 ? 'text-green-600' : 'text-gray-500'}`}>{t("instruction.status.connect")}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Settings className={`w-5 h-5 ${currentStep >= 1 ? 'text-green-500' : 'text-gray-400'}`} />
                <span className={`text-sm ${currentStep >= 1 ? 'text-green-600' : 'text-gray-500'}`}>{t("instruction.status.configure")}</span>
              </div>
              <div className="flex items-center space-x-2">
                <TrendingUp className={`w-5 h-5 ${currentStep >= 2 ? 'text-green-500' : 'text-gray-400'}`} />
                <span className={`text-sm ${currentStep >= 2 ? 'text-green-600' : 'text-gray-500'}`}>{t("instruction.status.analyze")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="flex items-center justify-between mt-12">
          <button
            onClick={prev}
            className="flex items-center space-x-2 px-6 py-3 bg-white rounded-xl shadow-lg hover:shadow-xl border-2 transition-all duration-300 hover:translate-y-[-2px]"
            style={{ 
              borderColor: darkBlue,
              color: darkBlue
            }}
            onMouseEnter={(e) => {
              const btn = e.target as HTMLButtonElement;
              btn.style.backgroundColor = darkBlue;
              btn.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              const btn = e.target as HTMLButtonElement;
              btn.style.backgroundColor = 'white';
              btn.style.color = darkBlue;
            }}
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-medium">{t("instruction.buttons.previous")}</span>
          </button>

          {/* Auto-play Toggle */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setAutoPlay(!autoPlay)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-300 border-2"
              style={{
                backgroundColor: autoPlay ? lightBlue : 'white',
                borderColor: lightBlue,
                color: autoPlay ? 'white' : lightBlue
              }}
            >
              <Play className={`w-4 h-4 ${autoPlay ? 'animate-pulse' : ''}`} />
              <span className="text-sm font-medium">{autoPlay ? t("instruction.buttons.autoPlaying") : t("instruction.buttons.autoPlay")}</span>
            </button>
            
            <div className="text-sm" style={{ color: darkBlue, opacity: 0.7 }}>
              {currentStep + 1}/{steps(t).length}
            </div>
          </div>

          <button
            onClick={next}
            className="flex items-center space-x-2 px-6 py-3 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:translate-y-[-2px]"
            style={{ backgroundColor: lightBlue }}
            onMouseEnter={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = darkBlue;
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = lightBlue;
            }}
          >
            <span className="font-medium">
              {currentStep === steps(t).length - 1 ? t("instruction.buttons.startOver") : t("instruction.buttons.next")}
            </span>
            {currentStep === steps(t).length - 1 ? (
              <ArrowRight className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Step Indicators (Dots) */}
        <div className="flex justify-center space-x-3 mt-8">
          {steps(t).map((_, index) => (
            <button
              key={index}
              onClick={() => handleStepChange(index)}
              className="w-3 h-3 rounded-full transition-all duration-300"
              style={{
                backgroundColor: index === currentStep ? lightBlue : 'rgba(16, 53, 100, 0.3)',
                transform: index === currentStep ? 'scale(1.25)' : 'scale(1)'
              }}
            />
          ))}
        </div>

        {/* Completion Message */}
        {currentStep === steps(t).length - 1 && (
          <div className="mt-12 text-center">
            <div 
              className="text-white rounded-2xl p-6 max-w-md mx-auto transform transition-transform duration-500 hover:scale-105"
              style={{ backgroundColor: darkBlue }}
            >
              <CheckCircle className="w-8 h-8 mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-2">{t("instruction.completion.title")}</h3>
              <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                {t("instruction.completion.description")}
              </p>
            </div>
          </div>
        )}

        
      </div>
    </div>
  );
};

export default UnifiedInstructionPage;